/**
 * Portal PDF download (P4) — replaces window.print().
 *
 * The server generates the file by printing THIS page, so what downloads is
 * what the viewer is looking at. Failure surfaces inline (the caller renders the
 * returned message), never as an alert() and never as a silent no-op.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/** True when Chromium is printing us — the portal hides its action buttons. */
export function isPrintMode(): boolean {
  return new URLSearchParams(window.location.search).get('print') === '1';
}

/**
 * STAFF PREVIEW (?preview=1) — the CRM's "Preview portal". The page renders
 * exactly as the client sees it, interactive layout and all, but nothing it
 * does is the client's: Accept is disabled, and the reads say they are a
 * preview so no tracking counts them as the client opening it.
 */
export function isStaffPreview(): boolean {
  return new URLSearchParams(window.location.search).get('preview') === '1';
}

export type DownloadState = { busy: boolean; error: string | null };

/**
 * Fetch the PDF and hand it to the browser. Returns an error message on failure
 * rather than throwing, so the caller can show it beside the button.
 */
export async function downloadPortalPdf(path: string): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}${path}`);
    if (!res.ok) {
      let msg = 'Could not prepare the PDF. Please try again, or call us and we will email it to you.';
      try {
        const body = await res.json();
        if (body?.error?.message) msg = body.error.message;
      } catch { /* non-JSON error body — keep the friendly default */ }
      return msg;
    }

    const blob = await res.blob();
    // Filename comes from Content-Disposition so the server owns the naming
    // convention in exactly one place.
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : 'BalticVanLines-Document.pdf';

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick; revoking synchronously cancels the download in
    // some browsers before it has started.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return null;
  } catch {
    return 'Could not prepare the PDF. Please check your connection and try again.';
  }
}
